import { Flag, Gavel, MessageCircleOff, Pin, PinOff, ShieldBan } from 'lucide-react'
import { useState } from 'react'

import type { Post } from '@/api/types'
import { MenuItem } from '@/components/ui/Menu'
import { useCurrentUser } from '@/features/auth'
import { ReportDialog, useAdminRemovePost, useSuspendToggle } from '@/features/moderation'
import { UserActionsMenu } from '@/features/relations'
import { useT } from '@/i18n'

import { useDeletePost, usePinToggle, useSetReplyPolicy } from '../hooks/usePostMutations'
import { ReplyPolicyDialog } from './ReplyPolicyPicker'

/**
 * 帖子右上角的「更多」菜单。
 *
 * 自己的帖子：置顶、改谁可以回复、删除。别人的帖子：静音、屏蔽、举报。
 * 管理员另外多两项直接处置 —— 真正的权限在后端，这里只决定显不显示。
 */
export function PostMenu({ post }: { post: Post }) {
  const t = useT()
  const { user } = useCurrentUser()
  const deletePost = useDeletePost()
  const pin = usePinToggle()
  const setPolicy = useSetReplyPolicy()
  const removePost = useAdminRemovePost()
  const suspend = useSuspendToggle()
  const [reporting, setReporting] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(false)

  const viewer = post.viewer
  if (!viewer) return null
  const isSelf = viewer.is_author
  const isAdmin = Boolean(user?.is_admin) && !isSelf
  // 回复设置属于整个线程，只能在第一条上改（D65）
  const isThreadRoot = !post.reply_to

  return (
    <>
      <UserActionsMenu
        target={post.author}
        isSelf={isSelf}
        onDelete={() => {
          if (window.confirm(t('post.confirmDelete'))) deletePost.mutate(post.id)
        }}
        extra={(close) =>
          isSelf && (
            <>
              <MenuItem
                onClick={() => {
                  close()
                  pin.mutate({ id: post.id, pinned: viewer.pinned })
                }}
              >
                {viewer.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
                {viewer.pinned ? t('menu.unpin') : t('menu.pin')}
              </MenuItem>
              {isThreadRoot && (
                <MenuItem
                  onClick={() => {
                    close()
                    setEditingPolicy(true)
                  }}
                >
                  <MessageCircleOff className="size-4" /> {t('menu.replyPolicy')}
                </MenuItem>
              )}
            </>
          )
        }
        trailing={(close) =>
          !isSelf && (
            <>
              <MenuItem
                onClick={() => {
                  close()
                  setReporting(true)
                }}
              >
                <Flag className="size-4" /> {t('menu.reportPost')}
              </MenuItem>
              {isAdmin && (
                <>
                  <MenuItem
                    danger
                    onClick={() => {
                      close()
                      if (window.confirm(t('menu.confirmAdminRemove'))) {
                        removePost.mutate({ postId: post.id })
                      }
                    }}
                  >
                    <Gavel className="size-4" /> {t('menu.adminRemove')}
                  </MenuItem>
                  <MenuItem
                    danger
                    onClick={() => {
                      close()
                      const username = post.author.username
                      if (window.confirm(t('menu.confirmAdminSuspend', { username }))) {
                        suspend.mutate({ username, suspended: false })
                      }
                    }}
                  >
                    <ShieldBan className="size-4" />
                    {t('menu.adminSuspend', { username: post.author.username })}
                  </MenuItem>
                </>
              )}
            </>
          )
        }
      />

      {/* 对话框挂在 body 上，但 React 事件仍沿组件树冒泡：不拦的话，
          在对话框里点一下会冒到帖子卡片上，直接跳进详情页 */}
      <div className="contents" onClick={(event) => event.stopPropagation()}>
        {/* 条件渲染：每次打开都从当前值重新开始，而不是沿用上次没保存的选择 */}
        {reporting && (
          <ReportDialog
            open
            onClose={() => setReporting(false)}
            target={post.author}
            postId={post.id}
          />
        )}
        {editingPolicy && (
          <ReplyPolicyDialog
            open
            initial={post.reply_policy}
            saving={setPolicy.isPending}
            onClose={() => setEditingPolicy(false)}
            onSave={(policy) =>
              setPolicy.mutate(
                { id: post.id, policy },
                { onSuccess: () => setEditingPolicy(false) },
              )
            }
          />
        )}
      </div>
    </>
  )
}
